import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CreditCard,
  Plus,
  Edit2,
  Trash2,
  XCircle,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react'
import { adminApi, AdminSubscription, CreateSubscriptionRequest, UpdateSubscriptionRequest } from '@/api/admin'
import type { SubscriptionType, SubscriptionStatus } from '@/types/api'

const SUBSCRIPTION_TYPES: SubscriptionType[] = ['one_time', 'monthly', 'quarterly', 'yearly', 'trial']
const SUBSCRIPTION_STATUSES: SubscriptionStatus[] = ['active', 'paused', 'expired', 'cancelled', 'suspended']

const TYPE_LABELS: Record<SubscriptionType, string> = {
  one_time: '일회성',
  monthly: '월간',
  quarterly: '분기',
  yearly: '연간',
  trial: '체험'
}

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: '활성',
  paused: '일시정지',
  expired: '만료',
  cancelled: '취소',
  suspended: '정지'
}

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-700',
  suspended: 'bg-orange-100 text-orange-700'
}

interface SubscriptionModalProps {
  subscription?: AdminSubscription | null
  onClose: () => void
  onSave: (data: CreateSubscriptionRequest | UpdateSubscriptionRequest) => void
  isLoading: boolean
}

function SubscriptionModal({ subscription, onClose, onSave, isLoading }: SubscriptionModalProps) {
  const [formData, setFormData] = useState<CreateSubscriptionRequest>({
    orderId: subscription?.orderId || 0,
    productId: subscription?.productId || 0,
    pricingId: subscription?.pricingId || 0,
    ownerUserId: subscription?.ownerUserId || 0,
    subscriptionType: subscription?.subscriptionType || 'monthly',
    status: subscription?.status || 'active',
    maxSeats: subscription?.maxSeats || 1,
    validFrom: subscription?.validFrom?.slice(0, 16) || new Date().toISOString().slice(0, 16),
    validUntil: subscription?.validUntil?.slice(0, 16) || '',
    autoRenew: subscription?.autoRenew ?? true
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (subscription) {
      const updateData: UpdateSubscriptionRequest = {
        status: formData.status,
        maxSeats: formData.maxSeats,
        validUntil: formData.validUntil || undefined,
        autoRenew: formData.autoRenew
      }
      onSave(updateData)
    } else {
      onSave(formData)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {subscription ? '구독 수정' : '새 구독 생성'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!subscription && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">주문 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.orderId}
                    onChange={(e) => setFormData({ ...formData, orderId: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <label className="label">상품 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">가격정책 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.pricingId}
                    onChange={(e) => setFormData({ ...formData, pricingId: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <label className="label">소유자 ID *</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.ownerUserId}
                    onChange={(e) => setFormData({ ...formData, ownerUserId: parseInt(e.target.value) })}
                    required
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">구독 유형 *</label>
              <select
                className="input"
                value={formData.subscriptionType}
                onChange={(e) => setFormData({ ...formData, subscriptionType: e.target.value as SubscriptionType })}
                disabled={!!subscription}
              >
                {SUBSCRIPTION_TYPES.map((type) => (
                  <option key={type} value={type}>{TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">상태 *</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as SubscriptionStatus })}
              >
                {SUBSCRIPTION_STATUSES.map((status) => (
                  <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label">최대 좌석 수</label>
            <input
              type="number"
              className="input"
              min={1}
              value={formData.maxSeats}
              onChange={(e) => setFormData({ ...formData, maxSeats: parseInt(e.target.value) })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">시작일 *</label>
              <input
                type="datetime-local"
                className="input"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                required
                disabled={!!subscription}
              />
            </div>
            <div>
              <label className="label">종료일</label>
              <input
                type="datetime-local"
                className="input"
                value={formData.validUntil || ''}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.autoRenew}
                onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                className="w-4 h-4 rounded-sm border-slate-300"
              />
              <span className="text-sm text-slate-700">자동 갱신</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : subscription ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function SubscriptionManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | ''>('')
  const [filterType, setFilterType] = useState<SubscriptionType | ''>('')
  const [modalSubscription, setModalSubscription] = useState<AdminSubscription | null | undefined>(undefined)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'subscriptions', { page, filterStatus, filterType }],
    queryFn: () => adminApi.getSubscriptions({
      page,
      size: 20,
      status: filterStatus || undefined,
      subscriptionType: filterType || undefined
    }),
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      setModalSubscription(undefined)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSubscriptionRequest }) =>
      adminApi.updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
      setModalSubscription(undefined)
    }
  })

  const cancelMutation = useMutation({
    mutationFn: (id: number) => adminApi.cancelSubscription(id, '관리자에 의한 취소'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteSubscription,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] })
  })

  const handleSave = (formData: CreateSubscriptionRequest | UpdateSubscriptionRequest) => {
    if (modalSubscription) {
      updateMutation.mutate({ id: modalSubscription.id, data: formData as UpdateSubscriptionRequest })
    } else {
      createMutation.mutate(formData as CreateSubscriptionRequest)
    }
  }

  const handleCancel = (subscription: AdminSubscription) => {
    if (confirm(`정말 이 구독을 취소하시겠습니까?`)) {
      cancelMutation.mutate(subscription.id)
    }
  }

  const handleDelete = (subscription: AdminSubscription) => {
    if (confirm(`정말 이 구독을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      deleteMutation.mutate(subscription.id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">구독 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-green-600" />
            구독 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}건의 구독</p>
        </div>
        <button
          onClick={() => setModalSubscription(null)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 구독
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            className="input w-40"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value as SubscriptionStatus | ''); setPage(0) }}
          >
            <option value="">모든 상태</option>
            {SUBSCRIPTION_STATUSES.map((status) => (
              <option key={status} value={status}>{STATUS_LABELS[status]}</option>
            ))}
          </select>
          <select
            className="input w-40"
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value as SubscriptionType | ''); setPage(0) }}
          >
            <option value="">모든 유형</option>
            {SUBSCRIPTION_TYPES.map((type) => (
              <option key={type} value={type}>{TYPE_LABELS[type]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">소유자</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">유형</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">좌석</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">유효기간</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((sub) => (
                <tr key={sub.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">{sub.id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{sub.ownerFullName}</p>
                      <p className="text-xs text-slate-500">{sub.ownerEmail}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {TYPE_LABELS[sub.subscriptionType]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[sub.status]}`}>
                      {STATUS_LABELS[sub.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {sub.usedSeats} / {sub.maxSeats}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(sub.validFrom).toLocaleDateString('ko-KR')}
                      {sub.validUntil && (
                        <span> ~ {new Date(sub.validUntil).toLocaleDateString('ko-KR')}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {sub.status === 'active' && (
                        <button
                          onClick={() => handleCancel(sub)}
                          className="p-2 hover:bg-yellow-50 rounded-lg text-yellow-600"
                          title="취소"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => setModalSubscription(sub)}
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                        title="수정"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(sub)}
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                        title="삭제"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {data?.content.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">등록된 구독이 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalSubscription !== undefined && (
        <SubscriptionModal
          subscription={modalSubscription}
          onClose={() => setModalSubscription(undefined)}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  )
}

